import React from 'react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Download, FileText } from 'lucide-react';
import { downloadExport } from './PricingShared';

const DashboardTab = ({ monthlyData, packages, heatmapData, API }) => {
  return (
    <div className="grid md:grid-cols-2 gap-6">
      {/* Monthly Stats */}
      <Card className="border-gray-200 bg-white">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-lg font-bold">Monthly Performance</h3>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => downloadExport(`${API}/api/pricing/export/monthly.csv`)} data-testid="export-monthly-csv">
              <Download className="h-4 w-4 mr-1" />CSV
            </Button>
            <Button variant="outline" size="sm" onClick={() => downloadExport(`${API}/api/pricing/export/monthly.pdf`)} data-testid="export-monthly-pdf">
              <FileText className="h-4 w-4 mr-1" />PDF
            </Button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Month</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Bookings</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Revenue</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Exotic Margin</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Red Zone</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Affiliate %</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {monthlyData.map((month) => (
                <tr key={month.month} className="hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm font-semibold">{month.month}</td>
                  <td className="py-3 px-4 text-sm text-right">{month.bookings_count}</td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-green-700">${month.total_revenue}</td>
                  <td className="py-3 px-4 text-sm text-right">{month.avg_exotic_margin}%</td>
                  <td className="py-3 px-4 text-sm text-right text-red-600 font-semibold">{month.red_zone_count}</td>
                  <td className="py-3 px-4 text-sm text-right">{month.affiliate_usage_percent}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          {monthlyData.length === 0 && <div className="text-center py-8 text-gray-500">No monthly data yet</div>}
        </div>
      </Card>

      {/* Transportation Packages */}
      <Card className="border-gray-200 bg-white">
        <div className="p-4 border-b border-gray-200"><h3 className="text-lg font-bold">Transportation Packages</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Hours</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Retail</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Affiliate</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">Member</th>
                <th className="text-right py-3 px-4 text-sm font-semibold text-gray-600">$/Hr</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {packages.map((pkg) => (
                <tr key={pkg.hours} className="hover:bg-gray-50">
                  <td className="py-3 px-4 text-sm font-semibold">{pkg.hours} hrs</td>
                  <td className="py-3 px-4 text-sm text-right font-semibold">${pkg.retail}</td>
                  <td className="py-3 px-4 text-sm text-right text-purple-600">${pkg.affiliate}</td>
                  <td className="py-3 px-4 text-sm text-right text-blue-600">${pkg.member}</td>
                  <td className="py-3 px-4 text-sm text-right">${pkg.retail_hourly}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Exotic Exposure Heatmap */}
      <Card className="md:col-span-2 border-gray-200 bg-white">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-bold">Exotic Exposure Heat Map</h3>
          <p className="text-sm text-gray-500">Daily exotic booking status overview</p>
        </div>
        <div className="p-4">
          <div className="flex flex-wrap gap-2">
            {heatmapData.map((day) => (
              <div key={day.date} className={`p-2 rounded text-xs text-center min-w-[85px] cursor-pointer hover:scale-105 transition-transform ${day.status === 'green' ? 'bg-green-100 border border-green-300 hover:bg-green-200' : day.status === 'yellow' ? 'bg-yellow-100 border border-yellow-300 hover:bg-yellow-200' : 'bg-red-100 border border-red-300 hover:bg-red-200'}`} title={`${day.count} bookings - $${day.revenue}`}>
                <div className="font-semibold">{day.date.slice(5)}</div>
                <div className="font-bold">${day.revenue}</div>
                <div className="text-[10px] text-gray-600">{day.count} booking{day.count !== 1 ? 's' : ''}</div>
              </div>
            ))}
            {heatmapData.length === 0 && <div className="text-gray-500 text-center w-full py-8">No exotic bookings yet. Add bookings with "exotic" service type to see the heatmap.</div>}
          </div>
          <div className="flex items-center gap-4 mt-4 pt-4 border-t">
            <span className="text-sm text-gray-500">Legend:</span>
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-green-200 border border-green-400"></div><span className="text-xs">Green (Profit &ge;$300)</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-yellow-200 border border-yellow-400"></div><span className="text-xs">Yellow ($200-$299)</span></div>
            <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-red-200 border border-red-400"></div><span className="text-xs">Red (&lt;$200)</span></div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default DashboardTab;
